import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Calendar, Building2, Phone, Globe, 
  MapPin, Clock, Edit2, Shield, FileText, Image, ExternalLink,
  Briefcase, Languages, Clock3, LogIn, CheckCircle, XCircle, Trash2, AlertOctagon
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TierBadge from '../components/TierBadge';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { resolveFileUrl } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminUserDetails = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { loginWithToken, user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ipCountry, setIpCountry] = useState(null);
  const [approving, setApproving] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Check if current user is super admin
  const isMainAdmin = currentUser?.role && ['admin', 'main_admin'].includes(currentUser.role);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  // Lookup country from IP
  useEffect(() => {
    if (user?.last_login_ip && user.last_login_ip !== '-') {
      lookupIpCountry(user.last_login_ip);
    }
  }, [user?.last_login_ip]);

  const lookupIpCountry = async (ip) => {
    try {
      // Use ip-api.com (free, no API key needed)
      const res = await fetch(`http://ip-api.com/json/${ip}?fields=status,country,countryCode`);
      const data = await res.json();
      if (data.status === 'success') {
        setIpCountry(data.country);
      }
    } catch (error) {
      console.log('IP lookup failed:', error);
    }
  };

  const handleLoginAs = async () => {
    try {
      const res = await api.post(`/admin/login-as/${userId}`);
      loginWithToken(res.data.access_token, res.data.user, true); // true = save admin token
      toast.success('Logged in as user. Click "Return to Admin" to go back.');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Failed to login as user');
    }
  };

  const handleHardDelete = async () => {
    // Double confirmation for hard delete
    if (!window.confirm(`⚠️ PERMANENT DELETION WARNING ⚠️\n\nAre you absolutely sure you want to PERMANENTLY DELETE user "${user.email}"?\n\nThis will remove:\n• User profile and login\n• All company data\n• All answers and documents\n• All support tickets\n• All deals and LOI requests\n• All activity logs\n• All invites and referrals\n\nThis action is IRREVERSIBLE!`)) {
      return;
    }
    
    // Second confirmation
    const confirmText = window.prompt(`Type "DELETE ${user.email}" to confirm permanent deletion:`);
    if (confirmText !== `DELETE ${user.email}`) {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }
    
    try {
      const res = await api.delete(`/admin/users/${userId}/hard-delete`);
      toast.success(`User permanently deleted. Removed data from: ${res.data.deleted_data?.deleted_collections?.join(', ') || 'all collections'}`);
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to permanently delete user');
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { action: 'approve' });
      toast.success(`${user.tier?.charAt(0).toUpperCase() + user.tier?.slice(1)} tier approved!`);
      fetchUserDetails(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    setApproving(true);
    try {
      await api.put(`/admin/tiers/${userId}/approve`, { 
        action: 'reject',
        rejection_reason: rejectReason || 'Application did not meet requirements'
      });
      toast.success(`${user.tier?.charAt(0).toUpperCase() + user.tier?.slice(1)} tier rejected`);
      setShowRejectForm(false);
      setRejectReason('');
      fetchUserDetails(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setApproving(false);
    }
  };

  const fetchUserDetails = async () => {
    try {
      const res = await api.get(`/admin/users/${userId}/details`);
      setUser(res.data);
    } catch (error) {
      toast.error('Failed to load user details');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAccountType = (type) => {
    if (!type) return 'Not set';
    return type.replace('_', '/').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Check if approval buttons should be shown for a specific tier
  const shouldShowApprovalButtons = (tier) => {
    return user?.tier === tier && user?.tier_status === 'pending';
  };

  // Approval buttons component
  const ApprovalButtons = ({ tier }) => {
    if (!shouldShowApprovalButtons(tier)) return null;
    
    return (
      <div className="flex items-center gap-2 ml-auto">
        <span className="text-xs px-2.5 py-1 rounded-full animate-pulse text-white" style={{ backgroundColor: BRAND_ORANGE }}>
          Pending Approval
        </span>
        <button
          onClick={handleApprove}
          disabled={approving}
          className="flex items-center gap-1 px-3 py-1.5 text-white text-sm font-medium rounded-xl transition disabled:opacity-50"
          style={{ backgroundColor: BRAND_BLUE }}
          data-testid={`approve-${tier}`}
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => setShowRejectForm(!showRejectForm)}
          disabled={approving}
          className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 text-sm font-medium rounded-xl hover:bg-red-100 transition disabled:opacity-50"
          data-testid={`reject-${tier}`}
        >
          <XCircle className="w-4 h-4" />
          Reject
        </button>
      </div>
    );
  };

  // Field display component
  const Field = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
      {Icon && <Icon className="w-5 h-5 text-gray-400 mt-0.5" />}
      <div className="flex-1">
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || <span className="text-gray-400 italic">Not provided</span>}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  if (!user) {
    return (
      <Sidebar>
        <div className="p-8 text-center">
          <p className="text-gray-500">User not found</p>
        </div>
      </Sidebar>
    );
  }

  const company = user.company;
  const documents = user.verification_documents || [];

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/users')}
              className="p-2.5 text-gray-500 hover:text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">User Details</h1>
              <p className="text-gray-500 text-sm mt-0.5">View and manage user information</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Hard Delete - Super Admin Only */}
            {isMainAdmin && user.role !== 'admin' && (
              <button
                onClick={handleHardDelete}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition"
                data-testid="hard-delete-user"
              >
                <AlertOctagon className="w-4 h-4" />
                Delete Permanently
              </button>
            )}
            {user.role !== 'admin' && (
              <button
                onClick={handleLoginAs}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition"
                data-testid="login-as-user"
              >
                <LogIn className="w-4 h-4" />
                Login as User
              </button>
            )}
            <Link
              to={`/admin/users/${userId}/edit`}
              className="flex items-center gap-2 text-white px-4 py-2.5 rounded-xl font-medium text-sm transition"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Edit2 className="w-4 h-4" />
              Edit User
            </Link>
          </div>
        </div>

        {/* Rejection Form - shown when reject is clicked */}
        {showRejectForm && (
          <div className="mb-6 p-5 bg-red-50 rounded-2xl border border-red-100">
            <h3 className="text-lg font-medium text-red-800 mb-3">Reject {user.tier?.charAt(0).toUpperCase() + user.tier?.slice(1)} Verification</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection (optional)"
              rows={3}
              className="w-full px-4 py-3 border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 mb-3 text-sm"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2">
              <button
                onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                className="px-4 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={approving}
                className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition disabled:opacity-50 text-sm font-medium"
                data-testid="confirm-reject"
              >
                {approving ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        )}

        {/* User Data Grid - notranslate to prevent user data from being translated */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 notranslate" translate="no">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* User Header Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden">
                  {user.profile_photo ? (
                    <img src={resolveFileUrl(user.profile_photo, BACKEND_URL)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </h2>
                  <p className="text-gray-500">{user.email}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <TierBadge tier={user.tier || 'none'} tierStatus={user.tier_status} size="sm" />
                    <span className="text-sm text-gray-500 capitalize">{user.role}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bronze Tier - User Profile */}
            <div className="bg-white rounded-2xl border border-orange-200 shadow-sm overflow-hidden" data-testid="bronze-section">
              <div className="bg-orange-50 px-6 py-4 border-b border-orange-200">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-orange-800 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Bronze Tier - User Profile
                  </h3>
                  <ApprovalButtons tier="bronze" />
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="First Name" value={user.first_name} icon={User} />
                  <Field label="Last Name" value={user.last_name} icon={User} />
                  <Field label="Email Address" value={user.email} icon={Mail} />
                  <Field label="Country" value={user.country} icon={Globe} />
                  <Field label="Account Type" value={formatAccountType(user.account_type)} icon={Briefcase} />
                  <Field label="Phone" value={user.phone} icon={Phone} />
                  <Field label="WhatsApp" value={user.whatsapp} icon={Phone} />
                  <div className="md:col-span-2">
                    <Field label="Description" value={user.description} />
                  </div>
                </div>
                {user.bronze_submitted_at && (
                  <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                    Submitted: {formatDate(user.bronze_submitted_at)}
                    {user.bronze_approved_at && <span className="ml-4">Approved: {formatDate(user.bronze_approved_at)}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Silver Tier - Company Details */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="silver-section">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-gray-700 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Silver Tier - Company Details
                  </h3>
                  <ApprovalButtons tier="silver" />
                </div>
              </div>
              <div className="p-6">
                {/* Individual Profile */}
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Individual Profile</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {company?.profile_photo && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 mb-2">Profile Photo / Company Logo</p>
                      <img 
                        src={resolveFileUrl(company.profile_photo, BACKEND_URL)}
                        alt="Logo" 
                        className="w-20 h-20 rounded-xl object-cover border border-gray-200"
                      />
                    </div>
                  )}
                  <Field label="Job Title / Role" value={company?.job_title} icon={Briefcase} />
                  <Field label="Phone Number" value={company?.phone_number} icon={Phone} />
                  <Field label="Preferred Language" value={company?.preferred_language} icon={Languages} />
                  <Field label="Time Zone" value={company?.timezone} icon={Clock3} />
                </div>
                
                {/* Company Details */}
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Company / Entity Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Field label="Company Legal Name" value={company?.company_legal_name} icon={Building2} />
                  </div>
                  <Field label="Country of Incorporation" value={company?.country_of_incorporation} icon={Globe} />
                  <Field label="Company Type" value={company?.company_type} icon={Briefcase} />
                  <div className="md:col-span-2">
                    <Field label="Company Address" value={company?.company_address} icon={MapPin} />
                  </div>
                  <div className="md:col-span-2">
                    {company?.company_website ? (
                      <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                        <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Website</p>
                          <a 
                            href={company.company_website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm font-medium hover:underline flex items-center gap-1"
                            style={{ color: BRAND_BLUE }}
                          >
                            {company.company_website} <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    ) : (
                      <Field label="Website" value={null} icon={Globe} />
                    )}
                  </div>
                </div>
                {user.silver_submitted_at && (
                  <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                    Submitted: {formatDate(user.silver_submitted_at)}
                    {user.silver_approved_at && <span className="ml-4">Approved: {formatDate(user.silver_approved_at)}</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Gold Tier - KYC/KYB */}
            <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden" data-testid="gold-section">
              <div className="bg-amber-50 px-6 py-4 border-b border-amber-200">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-amber-800 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Gold Tier - KYC/KYB Verification
                  </h3>
                  <ApprovalButtons tier="gold" />
                </div>
              </div>
              <div className="p-6">
                {/* KYC Info */}
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Identity Verification (KYC)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <Field label="Nationality" value={user.nationality} icon={Globe} />
                  <Field label="Tax ID / Registration Number" value={user.tax_id} icon={FileText} />
                </div>
                
                {/* Documents */}
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  Verification Documents ({documents.length})
                </h4>
                {documents.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {documents.map((doc, idx) => (
                      <div 
                        key={doc.id || idx} 
                        className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200"
                      >
                        {doc.content_type?.startsWith('image/') ? (
                          <Image className="w-5 h-5 text-amber-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-amber-600" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 capitalize">
                            {doc.document_type?.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{doc.original_filename}</p>
                        </div>
                        <a
                          href={resolveFileUrl(doc.file_url, BACKEND_URL)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1"
                          style={{ color: BRAND_BLUE }}
                          title="View document"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No documents uploaded</p>
                )}
                {user.gold_submitted_at && (
                  <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                    Submitted: {formatDate(user.gold_submitted_at)}
                    {user.gold_approved_at && <span className="ml-4">Approved: {formatDate(user.gold_approved_at)}</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Tier Status */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tier Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-600">Current Tier</span>
                  <TierBadge tier={user.tier || 'none'} tierStatus={user.tier_status} size="sm" />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`text-sm font-medium capitalize ${
                    user.tier_status === 'approved' ? 'text-green-600' :
                    user.tier_status === 'pending' ? 'text-amber-600' :
                    user.tier_status === 'rejected' ? 'text-red-600' :
                    'text-gray-600'
                  }`}>
                    {user.tier_status || 'Not Started'}
                  </span>
                </div>
                {user.tier_rejection_reason && (
                  <div className="p-3 bg-red-50 rounded-xl">
                    <p className="text-xs text-red-600">Rejection Reason</p>
                    <p className="text-sm text-red-800">{user.tier_rejection_reason}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Account Info</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Registered</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(user.created_at)?.split(',')[0]}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Last Login</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(user.last_login_at)?.split(',')[0] || '-'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Login Count</span>
                  <span className="text-sm font-medium text-gray-900">{user.login_count || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Last IP</span>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900 font-mono">{user.last_login_ip || '-'}</span>
                    {ipCountry && (
                      <span className="text-xs text-gray-500 ml-2">({ipCountry})</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity Log */}
            {user.activities && user.activities.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {user.activities.slice(0, 10).map((activity, idx) => (
                    <div key={idx} className="text-xs p-2.5 bg-gray-50 rounded-xl">
                      <span className="font-medium text-gray-700">{activity.action_type}</span>
                      <span className="text-gray-400 ml-2">
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminUserDetails;
